import { LightningElement, api } from 'lwc';

import { mapTemplateLabels, TEMPLATE_LABELS } from '../../../utils/languageUtil';
import {
    clearHeaderCache,
    getHeaderData,
    HEADER_DATA_CHANGE_EVENT,
    notifyHeaderDataChanged,
    updateHeaderDataCache,
} from '../../../utils/headerDataCache';

import {
    ADMIN_OPTIONS,
    USER_OPTIONS,
    DESTINATION_OPTIONS,
    GLOBAL_ACTION_OPTIONS_NON_USER,
    GLOBAL_ACTION_OPTIONS_USER,
    EVENT_OPTIONS,
} from '../../../utils/optionsUtil';

import {
    getStoredLocation,
    requestAndStoreLocation,
    getStoredApproxLocation,
    requestAndStoreApproxLocation,
} from '../../../utils/locationUtils';


import { patch } from 'aws-amplify/api';
import { signOut, fetchAuthSession } from 'aws-amplify/auth';


export default class getWetHeaderSearchBar extends LightningElement {
    minReservationDate = new Date();
    templateLabels = mapTemplateLabels(TEMPLATE_LABELS);

    _isLoading = false;

    adminOptions = ADMIN_OPTIONS;
    destinationOptions = DESTINATION_OPTIONS;
    eventOptions = EVENT_OPTIONS;

    get globalActionOptions() {
        if (!this.user) {
            return  GLOBAL_ACTION_OPTIONS_NON_USER;
        }
        return GLOBAL_ACTION_OPTIONS_USER;
    }
    get userOptions() {
        return USER_OPTIONS.map(option => {
            if (option.value !== 'messages') {
                return option;
            }

            return {
                ...option,
                label: this.unreadMessageCount > 0
                    ? `Messages (${this.unreadMessageCount} new)`
                    : 'Messages'
            };
        });
    }


    filterForm = {
        city: null,
        state: null,
        country: null,
        date: null,
        guests: null,
        latitude: null,
        longitude: null,
        searchMode: null
    };
    @api
    set destination(val) {
        if (val) {
            if (typeof val === 'string' && val.split(':')?.length === 3) {
                const [city, state, country] = val.split(':');
                this.filterForm = {...this.filterForm, city, state, country};
            }
        }
        this._destination = val;
    }
    get destination() {
        return this._destination ?? '';
    }
    @api
    set date(val) {
        this._date = val;
        this.filterForm = {...this.filterForm, date: val};
    }
    get date() {
        return this._date;
    }
    @api
    set guests(val) {
        this._guests = val;
        this.filterForm = {...this.filterForm, guests: val};
    }
    get guests() {
        return this._guests;
    }


    // Public properties to pass to google places component
    destinationComboboxProps = {
        fontSize: "1.0rem",
        placeHolder: "Destination",
        inputHeight: "var(--gw-input-height_large, 2.25rem)",
        borderRadius: "var(--slds-g-radius-border-pill, 15rem)",
        isSelectionVariant: false,
        optionsBordered: true,
        
        suppressIcon: true,
        dropdownDirection: "right",
        suppressBorder: true,

        suppressNoMatch: true,
        suppressSelectedIcon: true,
        dropdownGrow: true,
        suppressErrorText: true,
        allowNull: true,
        
    };

    @api getUserDetails() {
        return this.sessionUser ?? null;
    }



    //🎬 ========================= Initialize Page =========================== 🎬\\
    user;
    sessionUser;
    _idToken;
    unreadMessageCount = 0;
    async connectedCallback() {
        window.addEventListener('scroll', this.handleScroll, { passive: true });
        window.addEventListener(HEADER_DATA_CHANGE_EVENT, this.handleHeaderDataChanged);
        try {
            await this.getUserLocationApprox();
            await this.loadSessionUser();   
        } catch (e) {
            console.error('loadSessionUser() failed in getWetHeaderSearchBar.js:', e);
        }
    }
    disconnectedCallback() {
        window.removeEventListener('scroll', this.handleScroll);
        window.removeEventListener(HEADER_DATA_CHANGE_EVENT, this.handleHeaderDataChanged);
    }
    //🎬 ===================================================================== 🎬\\
    

    // city, location, region, lat, lng 
    //📍 ======================== Get User Location ========================== 📍\\
    userLocationPrecise;
    async getPreciseLocation() {
        this.userLocationPrecise = getStoredLocation();

        if (!this.userLocationPrecise) {
            try {
                this.userLocationPrecise = await requestAndStoreLocation();
                if (this.userLocationPrecise) {
                    console.log('Here is the location in initPage() of getWetHeaderSearchBar.js: ', this.userLocationPrecise);
                }
            }
            catch (error) {
                console.error(error);
                return;
            }
        }
    }
    userLocationApprox;
    async getUserLocationApprox() {
        this.userLocationApprox = await getStoredApproxLocation();

        if (!this.userLocationApprox) {
            this.userLocationApprox = await requestAndStoreApproxLocation();
        }

        console.log('Approximate location: ', this.userLocationApprox);
    }
    //📍 ===================================================================== 📍\\


    //✅ ========================= Authenticate User ========================= ✅\\
    applyHeaderData(headerData = {}) {
        if ('user' in headerData) {
            this.user = headerData.user || null;
        }
        if ('sessionUser' in headerData) {
            this.sessionUser = headerData.sessionUser || null;
        }
        if ('idToken' in headerData) {
            this._idToken = headerData.idToken || null;
        }
        if ('unreadMessageCount' in headerData) {
            this.unreadMessageCount = Number(headerData.unreadMessageCount || 0);
        }
    }
    handleHeaderDataChanged = (event) => {
        this.applyHeaderData(event.detail || {});
    };
    // Update unread app_user message count
    handleUnreadMessagesChange(message) {
        this.unreadMessageCount = Number(message.detail?.unreadMessageCount || 0);
        updateHeaderDataCache({ unreadMessageCount: this.unreadMessageCount });
        notifyHeaderDataChanged();
    }
    // Handle log in
    async handleUserAuthenticated(message) {
        this._isLoading = true;
        try {
            this.user = message.detail.user;
            const profile = message.detail?.profile;
            const modal = this.template.querySelector('x-get-wet-sign-up-modal');
            if (modal) {
                modal.close();
            }
            if (profile?.first_name && profile?.last_name) {
                await this.saveSessionUserProfile(profile);
            }
            clearHeaderCache();
            await this.loadSessionUser();
            notifyHeaderDataChanged();
        } catch(error) {
            this.showToast('Error in handleUserAuthenticated() of getWetHeaderSearchBar.js', error, 'error');
        } finally {
            this._isLoading = false;
        }
    }
    // Handle log out
    async handleSignOut() {
        this._isLoading = true;
        try {
            await signOut();
            this.user = null;
            this.sessionUser = null;
            this._idToken = null;
            this.unreadMessageCount = 0;
            clearHeaderCache();
            notifyHeaderDataChanged({
                user: null,
                sessionUser: null,
                idToken: null,
                unreadMessageCount: 0
            });
        } catch(error) {
            this.showToast('Error in handleSignOut() of getWetHeaderSearchBar.js', error, 'error');
        } finally {
            this._isLoading = false;
        }
    }
    async saveSessionUserProfile(fields) {
        const session = await fetchAuthSession();
        const idToken = session.tokens?.idToken?.toString();

        if (!idToken) {
            throw new Error('No idToken available to save profile.');
        }

        const op = patch({
            apiName: 'api',
            path: '/cognito/me',
            options: {
                headers: {
                    Authorization: `Bearer ${idToken}`,
                    'Content-Type': 'application/json'
                },
                body: fields
            }
        });

        await op.response;
    }
    // Get user basic info on sign-in
    async loadSessionUser() {
        this._isLoading = true;
        try {
            const headerData = await getHeaderData();
            this.applyHeaderData(headerData);
        } catch(error) {
            this.showToast('Error in loadSessionUser() of getWetHeaderSearchBar.js', error, 'error');
        } finally {
            this._isLoading = false;
        }
    }
    //✅ ===================================================================== ✅\\


    //📝 =========================== User Details ============================ 📝\\
    get isAdminUser() {
        return this.sessionUser?.role === 'admin'
    }
    get userProfilePhotoUrl() {
        return this.sessionUser?.profile_photo_url || this.user?.profile_photo_url || this.user?.picture || null;
    }
    get hasUserProfilePhoto() {
        return !!this.userProfilePhotoUrl;
    }
    get userProfilePhotoAlt() {
        const name = this.sessionUser?.display_name || this.sessionUser?.first_name || 'User';
        return `${name} profile photo`;
    }
    get isUserSignedIn() {
        return !!this.user;
    }
    get hasUnreadMessages() {
        return this.unreadMessageCount > 0;
    }
    get unreadMessageBadgeLabel() {
        return String(this.unreadMessageCount);
    }
    //📝 ===================================================================== 📝\\


    
    //🧭 ========================== Navigate Pages =========================== 🧭\\
    buildPageUrl(page, params = {}) {
        const url = new URL(window.location.href);
        url.searchParams.delete('listingId');
        url.searchParams.delete('hostListingId');

        if (page) {
            url.searchParams.set('page', page);
        } else {
            url.searchParams.delete('page');
        }

        Object.entries(params).forEach(([key, value]) => {
            if (value === null || value === undefined || value === '') {
                url.searchParams.delete(key);
            } else {
                url.searchParams.set(key, value);
            }
        });

        return url.toString();
    }
    dispatchPopState() {
        try {
            window.dispatchEvent(new PopStateEvent('popstate', { state: window.history.state }));
        } catch {
            window.dispatchEvent(new Event('popstate'));
        }
    }
    navigateToPage(page, params = {}) {
        window.history.pushState({}, '', this.buildPageUrl(page, params));
        this.dispatchPopState();
    }
    handleHomePageClick() {
        const url = new URL(window.location.href);
        url.searchParams.delete('listingId');
        url.searchParams.delete('hostListingId');
        url.searchParams.delete('page');
        window.history.replaceState({}, '', url.toString());
        this.dispatchPopState();
    }
    signUpHost() {
        this.navigateToPage('becomeHost');
    }
    handleLoginSignup() {
        this.template.querySelector('x-get-wet-sign-up-modal')?.open();
    }
    handleSignup() {
        this.template.querySelector('x-get-wet-sign-up-modal')?.open({ signUp: true });
    }
    showAccountDetails() {
        this.navigateToPage('accountDetails');
    }
    showGuestActivity() {
        this.navigateToPage('activity');
    }
    showHostHomePage() {
        this.navigateToPage('hostHomePage');
    }
    showMessageCenter() {
        this.navigateToPage('messages');
    }
    navToHelpCenter() {
        this.navigateToPage('helpCenter');
    }
    async handleUserAction(message) {
        const action = message.detail.value;

        switch(action) {
            case 'logOut':
                await this.handleSignOut();
                return;
            case 'accountDetails':
                this.showAccountDetails();
                return;
            case 'activity':
                this.showGuestActivity();
                return;
            case 'messages':
                this.showMessageCenter();
                return;
            case 'My Listings':
                this.showHostHomePage();
                return;
            default:
                return;
        }
    }
    async handleGetWetMenuSelection(message) {
        const selection = message.detail.value;

        switch(selection) {
            case 'loginSignup':
                this.handleLoginSignup();
                break;
            case 'signup':
                this.handleSignup();
                break;
            case 'becomeHost':
                this.signUpHost();
                break;
            case 'helpCenter':
                this.navToHelpCenter();
                break;
            case 'logOut':
                await this.handleSignOut();
                return;
            default:
                break;
        }
    }
    handleGetWetEventSelection(message) {
        console.log('Here is event message: ', JSON.stringify(message?.detail));
    }
    handleAdminAction(message) {
        const action = message.detail.value;

        switch(action) {
            case 'Listing Approvals':
                this.navigateToPage('listingApprovals');
                return;
            case 'Flagged Listing':
                this.navigateToPage('flaggedListings');
                return;
            default:
                return;
        }
    }
    //🧭 ===================================================================== 🧭\\



    //🔎 ======================== Search Bar Handlers ======================== 🔎\\
    handlePlaceChange(message) {
        const place = message.detail;
        if (!place) {
            this.filterForm = {
                ...this.filterForm,
                city: null,
                state: null,
                country: null,
                latitude: null,
                longitude: null,
                searchMode: null,
            };
            return;
        }
        const searchMode = place.searchMode;

        this.filterForm = {
            ...this.filterForm,
            city: place.city || null,
            state: place.state || null,
            country: place.country || null,
            latitude: place.latitude ?? null,
            longitude: place.longitude ?? null,
            searchMode
        };
    }
    handleReservationDate(message) {
        const value = message.detail?.value || null;
        this.filterForm = {
            ...this.filterForm,
            date: value
        };
    }
    handleGuests(message) {
        this.filterForm = {...this.filterForm, guests: message.detail.value};
    }
    async filterSearch() {
        this.dispatchEvent(new CustomEvent('searchlistings', {
            detail: {
                city: this.filterForm.city || null,
                state: this.filterForm.state || null,
                country: this.filterForm.country || null,
                latitude: this.filterForm.latitude ?? null,
                longitude: this.filterForm.longitude ?? null,
                searchMode: this.filterForm.searchMode || null,
                date: this.filterForm.date || null,
                guests: this.filterForm.guests ? Number(this.filterForm.guests) : null
            },
            bubbles: true,
            composed: true
        }));
    }
    //🔎 ===================================================================== 🔎\\

    

    //➡️ ========================= Layout Handlers ========================== ⬇️\\
    isCondensedHeader = false;
    _isExpandingHeader = false;
    get headerClass() {
        return this.isCondensedHeader
            ? 'slds-docked-form-header gw-header_condensed'
            : 'slds-docked-form-header';
    }
    get dockedMarginClass() {
        return this.isCondensedHeader
            ? 'docked-margin-small'
            : 'docked-margin-normal'
    }
    get scrollThresholds() {
        const isMobile = window.matchMedia?.('(max-width: 1000px)')?.matches;
        return isMobile
            ? { collapse: 48, expand: 16 }
            : { collapse: 48, expand: 16 };
    }
    get logoSize() {
        const isMobile = window.matchMedia?.('(max-width: 1000px)')?.matches;
        return isMobile
            ? '45'
            : '60';
    }
    handleScroll = () => {
        if (this._isExpandingHeader) {
            return;
        }

        const scrollY = window.scrollY;
        const { collapse, expand } = this.scrollThresholds;

        if (!this.isCondensedHeader && scrollY > collapse) {
            this.isCondensedHeader = true;
            return;
        }

        if (this.isCondensedHeader && scrollY < expand) {
            this._isExpandingHeader = true;
            window.scrollTo({
                top: 0,
                left: 0,
                behavior: 'auto'
            });

            requestAnimationFrame(() => {
                this.isCondensedHeader = false;
                this._isExpandingHeader = false;
            });
        }
    };
    //⬆️ ===================================================================== ⬅️\\


    
    toastObj;
    showToast(title, message, variant, mode=null) {
        const modeP = mode
            ? mode
                : variant === 'success'
            ? 'dismissible'
            : 'sticky';

        this.toastObj = {
            variant: variant,
            mode: modeP,
            message: message,
            header: {
                text: title
            }
        };
    }
}
